import apiClient from './meta-client';
import type {
    BroadcastResponse,
    PaginatedBroadcastGroups,
    BroadcastGroupDetail,
    PaginatedExecutions,
    CreateBroadcastRequest,
    UpdateBroadcastRequest,
    ValidateVariablesRequest,
    ValidateVariablesResponse,
    VariablesCopyRequest,
    VariablesCopyResponse,
    CancelBroadcastGroupResponse,
    PaginatedBroadcastRetries,
    UtilityOverlapCheckRequest,
    UtilityOverlapCheckResponse,
    BroadcastGroupSummary,
} from '@/types/broadcast';

// ========== BROADCASTS API ==========

// Listar todos os broadcasts do usuário autenticado (paginado por grupo)
export const getBroadcasts = async (
    page: number = 1,
    perPage: number = 20
): Promise<PaginatedBroadcastGroups> => {
    const response = await apiClient.get<PaginatedBroadcastGroups>('/meta/broadcasts', {
        params: { page, per_page: perPage },
    });
    return response.data;
};

// Listar broadcasts a nível de grupo (paginado)
/**
 * Converte o dia escolhido no calendario do usuario para o instante UTC
 * correspondente. O banco grava created_at em UTC: mandar a data crua faria
 * um broadcast criado 21h no BRT cair no dia seguinte.
 */
const diaLocalParaUtc = (dia: string, fim: boolean): string | undefined => {
    if (!dia) return undefined;
    const [ano, mes, d] = dia.split('-').map(Number);
    if (!ano || !mes || !d) return undefined;
    const data = fim
        ? new Date(ano, mes - 1, d, 23, 59, 59, 999)
        : new Date(ano, mes - 1, d, 0, 0, 0, 0);
    return data.toISOString().slice(0, 19).replace('T', ' ');
};

export const getBroadcastGroups = async (
    page: number = 1,
    perPage: number = 20,
    dateFrom?: string,
    dateTo?: string,
    userId?: number
): Promise<PaginatedBroadcastGroups> => {
    const response = await apiClient.get<PaginatedBroadcastGroups>('/meta/broadcasts/groups', {
        params: {
            page,
            per_page: perPage,
            date_from: diaLocalParaUtc(dateFrom ?? '', false),
            date_to: diaLocalParaUtc(dateTo ?? '', true),
            user_id: userId,
        },
    });
    return response.data;
};

export interface BroadcastCalendarResponse {
    items: BroadcastGroupSummary[];
    total: number;
    truncated: boolean;
}

/**
 * Grupos de um intervalo por data de AGENDAMENTO, para o calendario.
 *
 * Recebe e devolve instantes UTC; quem decide a que dia cada grupo pertence e
 * a tela, no fuso de Brasilia — o mesmo que os cards ja exibem.
 */
export const getBroadcastCalendar = async (
    scheduleFromUtc: string,
    scheduleToUtc: string,
    userId?: number
): Promise<BroadcastCalendarResponse> => {
    const response = await apiClient.get<BroadcastCalendarResponse>('/meta/broadcasts/calendar', {
        params: { schedule_from: scheduleFromUtc, schedule_to: scheduleToUtc, user_id: userId },
    });
    return response.data;
};

export interface BroadcastCalendarDay {
    dia: string;              // YYYY-MM-DD no fuso de Brasilia
    grupos: number;
    leads: number;
    enviados: number;
    falhas: number;
    grupos_em_aberto: number;
    amostra: string[];
}

/**
 * Totais por dia para a grade do mes.
 *
 * A grade nao precisa dos grupos — precisa do que cabe numa celula. Os grupos
 * de um dia vem de `getBroadcastCalendar` quando o dia e aberto.
 */
export const getBroadcastCalendarSummary = async (
    scheduleFromUtc: string,
    scheduleToUtc: string
): Promise<{ days: BroadcastCalendarDay[] }> => {
    const response = await apiClient.get<{ days: BroadcastCalendarDay[] }>(
        '/meta/broadcasts/calendar/summary',
        { params: { schedule_from: scheduleFromUtc, schedule_to: scheduleToUtc } },
    );
    return response.data;
};

export const getBroadcastGroupDetails = async (groupId: number): Promise<BroadcastGroupDetail> => {
    const response = await apiClient.get<BroadcastGroupDetail>(`/meta/broadcasts/group/${groupId}/details`);
    return response.data;
};

export const getBroadcastGroupExecutions = async (
    groupId: number,
    page: number = 1,
    perPage: number = 20,
    search?: string
): Promise<PaginatedExecutions> => {
    const params: Record<string, any> = { page, per_page: perPage };
    if (search) params.search = search;
    const response = await apiClient.get<PaginatedExecutions>(`/meta/broadcasts/group/${groupId}/executions`, {
        params,
    });
    return response.data;
};

// Listar todos os broadcasts (admin)
export const getAllBroadcasts = async (): Promise<BroadcastResponse[]> => {
    const response = await apiClient.get<BroadcastResponse[]>('/meta/broadcasts/all');
    return response.data;
};

// Obter broadcast específico
export const getBroadcast = async (broadcastId: number): Promise<BroadcastResponse> => {
    const response = await apiClient.get<BroadcastResponse>(`/meta/broadcasts/${broadcastId}`);
    return response.data;
};

// Criar novo broadcast
export const createBroadcast = async (data: CreateBroadcastRequest): Promise<BroadcastResponse> => {
    const response = await apiClient.post<BroadcastResponse>('/meta/broadcasts', data);
    return response.data;
};

// Esteira multi-template: calcula cobertura e sobreposição de páginas entre grupos selecionados
export const checkUtilityOverlap = async (
    data: UtilityOverlapCheckRequest
): Promise<UtilityOverlapCheckResponse> => {
    const response = await apiClient.post<UtilityOverlapCheckResponse>(
        '/meta/broadcasts/utility-overlap-check',
        data
    );
    return response.data;
};

// Atualizar broadcast
export const updateBroadcast = async (
    broadcastId: number,
    data: UpdateBroadcastRequest
): Promise<BroadcastResponse> => {
    const response = await apiClient.put<BroadcastResponse>(`/meta/broadcasts/${broadcastId}`, data);
    return response.data;
};

// Deletar broadcast (soft delete)
export const deleteBroadcast = async (broadcastId: number): Promise<{ message: string }> => {
    const response = await apiClient.delete<{ message: string }>(`/meta/broadcasts/${broadcastId}`);
    return response.data;
};

// Cancelar todos os broadcasts pendentes de um grupo
export const cancelBroadcastGroup = async (
    groupBroadcastId: number
): Promise<CancelBroadcastGroupResponse> => {
    const response = await apiClient.post<CancelBroadcastGroupResponse>(
        `/meta/broadcasts/group/${groupBroadcastId}/cancel`
    );
    return response.data;
};


// Validar variáveis das páginas para um template de broadcast
export const validateBroadcastVariables = async (
    data: ValidateVariablesRequest
): Promise<ValidateVariablesResponse> => {
    const response = await apiClient.post<ValidateVariablesResponse>('/meta/broadcasts/validate-variables', data);
    return response.data;
};

// Copiar variáveis de uma página para outras
export const copyPageVariables = async (
    data: VariablesCopyRequest
): Promise<VariablesCopyResponse> => {
    const response = await apiClient.post<VariablesCopyResponse>('/meta/pages/variables-copy', data);
    return response.data;
};

export async function getBroadcastRetries(params: {
    date: string;
    group_broadcast_id?: number;
    status?: string;
    page?: number;
    per_page?: number;
}): Promise<PaginatedBroadcastRetries> {
    const p: Record<string, unknown> = { date: params.date };
    if (params.group_broadcast_id) p.group_broadcast_id = params.group_broadcast_id;
    if (params.status) p.status = params.status;
    if (params.page) p.page = params.page;
    if (params.per_page) p.per_page = params.per_page;
    const response = await apiClient.get('/meta/broadcasts/retries', { params: p });
    return response.data;
}

// ========== PAGES API ==========


export interface UtilityTemplate {
    id: number;
    template_name: string;
    language: string;
    status: string;
    title: string;
    subtitle: string;
    image_path?: string | null;
    image_url?: string | null;
    group_id?: number | null;
}

export interface UtilityTemplateGroup {
    id: number;
    template_name: string;
    title: string;
    subtitle: string;
    user_id: number;
    image_url?: string | null;
    thumbnail_url?: string | null;
    buttons?: Array<{ type: string; text: string; url_base?: string }>;
    source_language?: string | null;
    created_at?: string;
    // contadores calculados localmente a partir de submissions
    approved_count?: number;
    owner_name?: string | null;
    is_mine?: boolean;
}

/** Lista todos os utility templates (opcionalmente filtrado por page_id) */
export const getUtilityTemplates = async (pageId?: string): Promise<UtilityTemplate[]> => {
    const response = await apiClient.get<UtilityTemplate[]>('/meta/utility-templates', {
        params: pageId ? { page_id: pageId } : undefined,
    });
    return response.data;
};

/** Lista os grupos de utility templates — a biblioteca inteira, ou só os do usuário logado com `onlyMine`. */
export const getUtilityTemplateGroupsAll = async (onlyMine = false): Promise<UtilityTemplateGroup[]> => {
    const response = await apiClient.get<UtilityTemplateGroup[]>('/meta/utility-templates/groups', {
        params: onlyMine ? { only_mine: true } : undefined,
    });
    return response.data;
};


// Obter imagem da página diretamente do Meta Graph API
export const getPagePicture = async (pageId: string, accessToken: string): Promise<string> => {
    try {
        const response = await fetch(
            `https://graph.facebook.com/v18.0/${pageId}/picture?type=large&redirect=false&access_token=${accessToken}`
        );

        if (!response.ok) {
            throw new Error('Erro ao carregar imagem da página');
        }

        const data = await response.json();
        return data.data.url;
    } catch (error) {
        console.error('Erro ao buscar imagem da página:', error);
        throw error;
    }
};
